/**
 * OverviewSection — Role overview with basic info & settings
 *
 * Fields: Role ID (read-only), Display Name, Display Order,
 * Description, Role Color, Status, System Default Role, System badge
 */

import {
  Grid,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import CollapsibleSection from "templates/FullPageDetailTemplate/CollapsibleSection";
import { useMaterialUIController } from "context";
import { useT } from "context/I18nContext";

const ROLE_COLORS = [
  { value: "#EF4444", label: "Red" },
  { value: "#F59E0B", label: "Amber" },
  { value: "#10B981", label: "Green" },
  { value: "#0EA5E9", label: "Blue" },
  { value: "#0D9488", label: "Teal" },
  { value: "#8B5CF6", label: "Purple" },
  { value: "#EC4899", label: "Pink" },
  { value: "#78716C", label: "Gray" },
];

function OverviewSection({ roleData, updateField, expanded, onToggle, canEdit }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = useT();

  const labelSx = {
    color: darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
    fontSize: "0.75rem",
    fontWeight: 600,
    mb: 0.5,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const valueSx = {
    color: darkMode ? "white" : "text.main",
    fontSize: "0.95rem",
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
      borderRadius: "12px",
      "& fieldset": { borderColor: darkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)" },
      "&:hover fieldset": { borderColor: darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)" },
      "&.Mui-focused fieldset": { borderColor: "primary.main" },
    },
    "& .MuiInputLabel-root": { color: darkMode ? "rgba(255,255,255,0.6)" : undefined },
    "& .MuiOutlinedInput-input": { color: darkMode ? "white" : undefined },
  };

  return (
    <CollapsibleSection
      title={t("roles.detail.overview")}
      icon={<InfoIcon sx={{ color: "white", fontSize: 20 }} />}
      iconGradient={["#0EA5E9", "#7DD3FC"]}
      expanded={expanded}
      onToggle={onToggle}
    >
      <Grid container spacing={3}>
        {/* Role ID (read-only) */}
        <Grid item xs={12} md={4}>
          <KDTypography sx={labelSx}>{t("roles.detail.roleId")}</KDTypography>
          <KDTypography sx={valueSx}>{roleData?.id || "N/A"}</KDTypography>
          <KDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>
            {t("roles.detail.cannotChange")}
          </KDTypography>
        </Grid>

        {/* Display Name */}
        <Grid item xs={12} md={4}>
          <KDTypography sx={labelSx}>{t("roles.name")}</KDTypography>
          {canEdit && !roleData?.isSystem ? (
            <TextField
              size="small"
              fullWidth
              value={roleData?.name || ""}
              onChange={(e) => updateField("name", e.target.value)}
              sx={inputSx}
            />
          ) : (
            <KDTypography sx={valueSx}>{roleData?.name || "N/A"}</KDTypography>
          )}
        </Grid>

        {/* Display Order */}
        <Grid item xs={12} md={4}>
          <KDTypography sx={labelSx}>{t("roles.detail.order")}</KDTypography>
          {canEdit ? (
            <TextField
              type="number"
              size="small"
              fullWidth
              value={roleData?.order || 0}
              onChange={(e) => updateField("order", parseInt(e.target.value, 10) || 0)}
              sx={inputSx}
              inputProps={{ min: 0 }}
            />
          ) : (
            <KDTypography sx={valueSx}>{roleData?.order || 0}</KDTypography>
          )}
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <KDTypography sx={labelSx}>{t("roles.description_label")}</KDTypography>
          {canEdit ? (
            <TextField
              multiline
              rows={3}
              fullWidth
              value={roleData?.description || ""}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder={t("roles.detail.descriptionPlaceholder")}
              sx={inputSx}
            />
          ) : (
            <KDTypography sx={valueSx}>
              {roleData?.description || t("roles.detail.noDescription")}
            </KDTypography>
          )}
        </Grid>

        {/* Color */}
        <Grid item xs={12} md={6}>
          <KDTypography sx={labelSx}>{t("roles.detail.color")}</KDTypography>
          {canEdit ? (
            <KDBox display="flex" gap={1} flexWrap="wrap">
              {ROLE_COLORS.map((color) => (
                <KDBox
                  key={color.value}
                  onClick={() => updateField("color", color.value)}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    bgcolor: color.value,
                    cursor: "pointer",
                    border: roleData?.color === color.value
                      ? `3px solid ${darkMode ? "white" : "#1C1917"}`
                      : "3px solid transparent",
                    transition: "all 0.2s",
                    "&:hover": { transform: "scale(1.15)" },
                  }}
                  title={color.label}
                />
              ))}
            </KDBox>
          ) : (
            <KDBox
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                bgcolor: roleData?.color || "#78716C",
              }}
            />
          )}
        </Grid>

        {/* Status */}
        <Grid item xs={12} md={3}>
          <KDTypography sx={labelSx}>{t("common.status")}</KDTypography>
          <FormControlLabel
            control={
              <Switch
                checked={roleData?.isActive !== false}
                onChange={(e) => updateField("isActive", e.target.checked)}
                disabled={!canEdit || roleData?.isSystem}
                color="success"
              />
            }
            label={
              <KDTypography variant="body2" color={darkMode ? "white" : "dark"}>
                {roleData?.isActive !== false ? t("common.active") : t("common.inactive")}
              </KDTypography>
            }
          />
        </Grid>

        {/* Default role toggle */}
        <Grid item xs={12} md={3}>
          <KDTypography sx={labelSx}>{t("roles.detail.defaultRole")}</KDTypography>
          <FormControlLabel
            control={
              <Switch
                checked={roleData?.isDefaultRole === true}
                onChange={(e) => updateField("isDefaultRole", e.target.checked)}
                disabled={!canEdit}
                color="info"
              />
            }
            label={
              <KDTypography variant="body2" color={darkMode ? "white" : "dark"}>
                {roleData?.isDefaultRole
                  ? t("roles.detail.isDefault")
                  : t("roles.detail.notDefault")}
              </KDTypography>
            }
          />
          <KDTypography variant="caption" color="text" sx={{ opacity: 0.5, display: "block", mt: 0.5 }}>
            {t("roles.detail.defaultRoleHint")}
          </KDTypography>
        </Grid>

        {/* System role indicator */}
        {roleData?.isSystem && (
          <Grid item xs={12}>
            <KDBox
              sx={{
                bgcolor: darkMode ? "rgba(245,158,11,0.1)" : "rgba(245,158,11,0.08)",
                border: "1px solid rgba(245,158,11,0.3)",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <KDTypography variant="body2" color={darkMode ? "white" : "dark"}>
                🔒 {t("roles.systemNote")}
              </KDTypography>
            </KDBox>
          </Grid>
        )}
      </Grid>
    </CollapsibleSection>
  );
}

export default OverviewSection;
